import {
  NoteDeleteIcon,
  ReportRowSeparator,
  TabReportsIcon,
} from "./icons/patientDetailIcons";
import { InlineLoading } from "./loading/LoadingIndicators";
import { CSS_CLASSES, MESSAGES } from "../constants/app";
import type { Report } from "../types";
import {
  formatReportDate,
  formatReportTypeLabel,
  getReportResultText,
  getReportResultTone,
  getReportResultToneClassName,
} from "../utils/reportDisplay";

export type SortedReport = Omit<Report, "createdAt"> & {
  reportType: string;
  createdAt: Date;
};

type ReportVariant = "original" | "derivative";
type ReportFetchMode = "view" | "download";

export interface ReportsTabProps {
  sortedReports: SortedReport[];
  reportAnalysis: Record<string, unknown>;
  reportsLoading: boolean;
  reportsError: string | null;
  reportActionError: string | null;
  onRetry: () => void;
  onUploadReport: () => void;
  onReportClick: (reportType: string, fileName: string) => void;
  onRequestDelete: (report: SortedReport) => void;
  onFetchReportVariant: (params: {
    reportType: string;
    fileName: string;
    variant?: ReportVariant;
    mode?: ReportFetchMode;
    fallbackFileName?: string;
  }) => void;
}

function getReportAnalysisData(
  report: SortedReport,
  reportAnalysis: Record<string, unknown>
): Record<string, unknown> | null {
  if (report.data && typeof report.data === "object") {
    return report.data as Record<string, unknown>;
  }
  const key = `${report.reportType}_${report._id}`;
  const fromAnalysis = reportAnalysis[key];
  if (fromAnalysis && typeof fromAnalysis === "object") {
    return fromAnalysis as Record<string, unknown>;
  }
  return null;
}

export default function ReportsTab({
  sortedReports,
  reportAnalysis,
  reportsLoading,
  reportsError,
  reportActionError,
  onRetry,
  onUploadReport,
  onReportClick,
  onRequestDelete,
  onFetchReportVariant,
}: ReportsTabProps) {
  const renderBodyContent = () => {
    if (reportsLoading) {
      return <InlineLoading message={MESSAGES.LOADING.REPORTS} />;
    }

    if (reportsError) {
      return (
        <div className="rounded-xl border border-dashed border-guardian-20 bg-guardian-10 p-6 text-center">
          <p className="text-sm text-rag-red" role="alert">
            {reportsError}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className={`mt-4 ${CSS_CLASSES.BUTTONS.CANCEL_MODAL}`}
          >
            Try again
          </button>
        </div>
      );
    }

    if (sortedReports.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-guardian-20 bg-guardian-10 p-6 text-center">
          <TabReportsIcon className="mx-auto mb-3 h-8 w-8 opacity-60" />
          <p className="text-sm font-medium text-guardian-body">
            {MESSAGES.EMPTY_STATES.NO_REPORTS}
          </p>
          <p className={`mt-1 ${CSS_CLASSES.TEXT.MUTED}`}>
            {MESSAGES.EMPTY_STATES.NO_REPORTS_HELPER}
          </p>
          <button
            type="button"
            onClick={onUploadReport}
            className={`mt-4 ${CSS_CLASSES.BUTTONS.SECONDARY_PATIENT}`}
          >
            Upload reports
          </button>
        </div>
      );
    }

    return (
      <>
        {reportActionError && (
          <div className="mb-2 shrink-0 text-sm text-rag-red" role="alert">
            {reportActionError}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {sortedReports.map((report) => {
            const reportAnalysisData = getReportAnalysisData(
              report,
              reportAnalysis
            );
            const resultText = getReportResultText(
              report.reportType,
              reportAnalysisData
            );
            const tone = getReportResultTone(
              report.reportType,
              reportAnalysisData
            );
            const toneClass = getReportResultToneClassName(tone);
            const isChart = report.reportType === "chart";

            return (
              <div
                key={report._id}
                className="group -mx-3 flex cursor-pointer flex-col items-start gap-2.5 rounded-lg border-b border-guardian-10 px-3 py-2.5 transition-colors hover:bg-guardian-10/60 sm:flex-row sm:flex-wrap sm:items-start"
                onClick={() =>
                  onReportClick(report.reportType, report.fileName)
                }
              >
                <div className="flex min-w-0 w-full flex-1 flex-col gap-0.5 px-1">
                  <div className="text-[12px] leading-4 tracking-[0.4px] text-guardian-header">
                    {formatReportDate(report.createdAt)}
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 text-sm font-medium text-guardian-body">
                      {formatReportTypeLabel(
                        report.reportType,
                        report.reportTypeLabel
                      )}
                    </span>
                    {resultText != null && (
                      <>
                        <ReportRowSeparator />
                        <span
                          className={`min-w-0 truncate text-sm font-normal tracking-[-0.013em] ${toneClass}`}
                        >
                          {resultText}
                        </span>
                      </>
                    )}
                  </div>
                  {isChart && (
                    <div
                      className="mt-2 flex w-full flex-wrap gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                    <button
                      type="button"
                      className="rounded-md bg-guardian-20 px-2 py-1 text-xs text-guardian-body transition-colors hover:bg-guardian-20/70"
                      onClick={() =>
                        onFetchReportVariant({
                          reportType: report.reportType,
                          fileName: report.fileName,
                          variant: "original",
                          mode: "view",
                        })
                      }
                    >
                      View Original
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-200 disabled:cursor-not-allowed disabled:bg-guardian-10 disabled:text-guardian-header"
                      disabled={
                        !report.derivativeFileName && !report.derivativeFilePath
                      }
                      title={
                        !report.derivativeFileName && !report.derivativeFilePath
                          ? "Derivative not available"
                          : "View SuperECG"
                      }
                      onClick={() =>
                        onFetchReportVariant({
                          reportType: report.reportType,
                          fileName: report.fileName,
                          variant: "derivative",
                          mode: "view",
                        })
                      }
                    >
                      View SuperECG
                    </button>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2 self-end opacity-100 focus-within:opacity-100 sm:self-auto sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                  <button
                    type="button"
                    className="rounded p-1 transition-colors hover:bg-guardian-20"
                    title="Delete report"
                    aria-label="Delete report"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDelete(report);
                    }}
                  >
                    <NoteDeleteIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {renderBodyContent()}
    </div>
  );
}
